import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  fetchUserProtocols,
  searchUserProtocols,
  deleteUserProtocol,
  updateExistingProtocol,
  setFilters,
  setCurrentPage,
  setItemsPerPage,
  resetFilters,
} from '../../../../protocolSlice';
import ProtocolCard from './ProtocolCard';
import ProtocolFilters from './ProtocolFilters';
import CreateProtocolButton from './CreateProtocolButton';
import ProtocolPreviewDialog from './ProtocolPreviewDialog';
import './ProtocolList.scss';

const ProtocolList: React.FC = () => {
  const dispatch = useAppDispatch() as any;
  const navigate = useNavigate();

  const {
    userProtocols,
    totalUserProtocols,
    loading,
    deleting,
    filters,
    currentPage,
    itemsPerPage,
    totalPages,
    hasNext,
    hasPrev,
    error,
  } = useAppSelector((state) => state.protocols);


  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState<number | null>(null);
  const [specialties, setSpecialties] = useState<string[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastRequestRef = useRef<string>('');

  // Extract unique specialties from protocols
  useEffect(() => {
    if (!userProtocols || !Array.isArray(userProtocols)) {
      setSpecialties([]);
      return;
    }

    const uniqueSpecialties = Array.from(
      new Set(
        userProtocols
          .map((protocol: any) => protocol.specialty)
          .filter(Boolean) as string[]
      )
    ).sort();
    setSpecialties(uniqueSpecialties);
  }, [userProtocols]);



  // Effect for filters and pagination changes
  useEffect(() => {
    // Предотвращаем запросы во время загрузки
    if (loading) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const fullFilters = {
      specialty: filters.specialty,
      status: filters.status,
      search: filters.search,
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage
    };

    // Создаем уникальный ключ запроса для предотвращения дублирования
    const requestKey = JSON.stringify(fullFilters);

    // Проверяем, не был ли такой же запрос уже отправлен
    if (lastRequestRef.current === requestKey) {
      return;
    }

    lastRequestRef.current = requestKey;

    if (filters.search) {
      // Debounce search queries
      debounceRef.current = setTimeout(() => {
        dispatch(searchUserProtocols({ query: filters.search, filters: fullFilters }));
      }, 500);
    } else {
      // Immediate fetch for non-search filters
      dispatch(fetchUserProtocols(fullFilters));
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [dispatch, filters.specialty, filters.status, filters.search, currentPage, itemsPerPage]);

  const handleFiltersChange = useCallback((newFilters: Partial<typeof filters>) => {
    dispatch(setFilters(newFilters));
  }, [dispatch]);

  const handleResetFilters = useCallback(() => {
    dispatch(resetFilters());
  }, [dispatch]);

  const handlePageChange = useCallback((page: number) => {
    dispatch(setCurrentPage(page));
  }, [dispatch]);

  const handleItemsPerPageChange = useCallback((itemsPerPage: number) => {
    dispatch(setItemsPerPage(itemsPerPage));
  }, [dispatch]);

  const handleRetryLoad = () => {
    if (filters.search) {
      dispatch(searchUserProtocols({ query: filters.search, filters }));
    } else {
      dispatch(fetchUserProtocols(filters));
    }
  };

  const handleViewProtocol = (id: number) => {
    setShowPreviewDialog(id);
  };

  const handleEditProtocol = (id: number) => {
    // Navigate to protocol edit page
    navigate(`/protocols/${id}/edit`);
  };

  const handleDeleteProtocol = (id: number) => {
    setShowDeleteConfirm(id);
  };

  const confirmDelete = async (id: number) => {
    try {
      await dispatch(deleteUserProtocol(id));
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete protocol:', error);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const handleToggleStatus = async (id: number, isActive: boolean) => {
    try {
      await dispatch(updateExistingProtocol({
        id,
        protocol: { is_active: isActive }
      }));
    } catch (error) {
      console.error('Failed to toggle protocol status:', error);
    }
  };

  // totalPages теперь приходит с бэкенда, не вычисляем локально

  // Обработка ошибки загрузки
  if (error && (!userProtocols || userProtocols.length === 0)) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100">

        <div className="error-icon mb-4">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#e74c3c" strokeWidth="2" />
            <line x1="15" y1="9" x2="9" y2="15" stroke="#e74c3c" strokeWidth="2" />
            <line x1="9" y1="9" x2="15" y2="15" stroke="#e74c3c" strokeWidth="2" />
          </svg>
        </div>
        <h3>Не удалось загрузить шаблоны</h3>
        <p>Возникла проблема при загрузке данных, повторите попытку позже</p>
        <div className="error-actions">
          <button
            className="btn btn-primary"
            onClick={handleRetryLoad}
            disabled={loading}
          >
            {loading ? 'Загрузка...' : 'Обновить'}
          </button>
        </div>

      </div>
    );
  }

  // Обработка загрузки
  if (loading && (!userProtocols || userProtocols.length === 0)) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
          <p>Загрузка шаблонов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="protocols-header">
        <div className="header-left">
          <h1>Мои шаблоны</h1>
        </div>
        <CreateProtocolButton />
      </div>

      <div className="page-content">
        <div className="protocols-section">

          <ProtocolFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onReset={handleResetFilters}
            specialties={specialties}
          />

          {error && (
            <div className="error-banner">
              <div className="error-banner__content">
                <div className="error-banner__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" />
                    <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <div className="error-banner__text">
                  <strong>Ошибка загрузки:</strong> Не удалось обновить данные
                </div>
                <button
                  className="error-banner__retry"
                  onClick={handleRetryLoad}
                  disabled={loading}
                >
                  {loading ? 'Обновление...' : 'Обновить'}
                </button>
              </div>
            </div>
          )}

          {!userProtocols || userProtocols.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    stroke="#6c757d"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="14,2 14,8 20,8"
                    stroke="#6c757d"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="16"
                    y1="13"
                    x2="8"
                    y2="13"
                    stroke="#6c757d"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="16"
                    y1="17"
                    x2="8"
                    y2="17"
                    stroke="#6c757d"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="10,9 9,9 8,9"
                    stroke="#6c757d"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>У вас пока нет шаблонов</h3>
              <p>Создайте свой первый шаблон для начала работы</p>
            </div>
          ) : (
            <>
              <div className="protocol-list__content">
                {userProtocols && userProtocols.map((protocol: any) => (
                  <ProtocolCard
                    key={protocol.id}
                    protocol={protocol}
                    onView={handleViewProtocol}
                    onEdit={handleEditProtocol}
                    onDelete={handleDeleteProtocol}
                    onToggleStatus={handleToggleStatus}
                  />
                ))}
              </div>

              {userProtocols && userProtocols.length > 0 && (
                <div className="protocol-list__pagination">
                  <div className="pagination-info">
                    <div>Показано {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalUserProtocols)} из {totalUserProtocols}</div>
                  </div>
                  <div className="pagination-controls">
                    {totalPages > 1 && (
                      <nav aria-label="Навигация по страницам">
                        <ul className="pagination">
                          <li className={`page-item ${!hasPrev ? 'disabled' : ''}`}>
                            <button
                              className="page-link nav-button"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={!hasPrev}
                            >
                              &lt; Назад
                            </button>
                          </li>

                          {/* Первая страница */}
                          {currentPage > 3 && (
                            <>
                              <li className="page-item">
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(1)}
                                >
                                  1
                                </button>
                              </li>
                              {currentPage > 4 && (
                                <li className="page-item">
                                  <span className="page-link page-dots">...</span>
                                </li>
                              )}
                            </>
                          )}

                          {/* Страницы вокруг текущей */}
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                              const start = Math.max(1, currentPage - 1);
                              const end = Math.min(totalPages, currentPage + 1);
                              return page >= start && page <= end;
                            })
                            .map(page => (
                              <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ))}

                          {/* Последняя страница */}
                          {currentPage < totalPages - 2 && (
                            <>
                              {currentPage < totalPages - 3 && (
                                <li className="page-item">
                                  <span className="page-link page-dots">...</span>
                                </li>
                              )}
                              <li className="page-item">
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(totalPages)}
                                >
                                  {totalPages}
                                </button>
                              </li>
                            </>
                          )}

                          <li className={`page-item ${!hasNext ? 'disabled' : ''}`}>
                            <button
                              className="page-link nav-button"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={!hasNext}
                            >
                              Далее &gt;
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="modal-overlay" onClick={cancelDelete}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h5>Подтверждение удаления</h5>
                </div>
                <div className="modal-body">
                  <p>Вы уверены, что хотите удалить этот шаблон? Это действие нельзя отменить.</p>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={cancelDelete}
                    disabled={deleting}
                  >
                    Отмена
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => confirmDelete(showDeleteConfirm)}
                    disabled={deleting}
                  >
                    {deleting ? 'Удаление...' : 'Удалить'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Protocol Preview Dialog */}
          {showPreviewDialog && (
            <ProtocolPreviewDialog
              protocol={userProtocols.find((p: any) => p.id === showPreviewDialog)!}
              isOpen={!!showPreviewDialog}
              onClose={() => setShowPreviewDialog(null)}
              onEdit={handleEditProtocol}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProtocolList;
